import React, { useState, useMemo, useCallback } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { observer } from "mobx-react-lite";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Box,
  HStack,
  VStack,
  Flex,
  Text,
  Input,
  Button,
  Link,
  useToast,
} from "@chakra-ui/react";

import { useStores } from "../../store/store_context";
import ColumnFilters from "./column_filters";

const TableData = observer(({ data = [], renderButtons }) => {
  const { pageStore } = useStores();
  const toast = useToast();

  const [sortConfig, setSortConfig] = useState({
    key: "BeginDate",
    direction: "desc",
  });

  const availableColumns = useMemo(
    () => pageStore.columnConfigOrderSuppliers.filter((c) => c.active),
    [pageStore.columnConfigOrderSuppliers]
  );

  // --- клиентская фильтрация уже пришедшей таблицы (точное совпадение) ---
  // Поля фильтров живут в сторе, мобиксовые, поэтому пересчёт реактивный.
  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) return [];

    const eq = (cellValue, filterValue) => {
      if (filterValue == null || String(filterValue).trim() === "") return true; // фильтр пуст — пропускаем всё
      if (cellValue == null) return false;
      // точное совпадение, без учёта регистра и крайних пробелов
      return String(cellValue).trim().toLowerCase().includes(String(filterValue).trim().toLowerCase());
    };

    const fInternal = pageStore.os_internal_code_filter;
    const fPayment = pageStore.os_payment_code_filter;
    const fDeal = pageStore.os_id_deal_filter;
    const fAccount = pageStore.os_account_num_1c_filter;

    return data.filter((row) =>
      eq(row?.InternalCode, fInternal) &&
      eq(row?.PaymentCodeB24, fPayment) &&
      // ID сделки в данных лежит в IdDeal (parentId2)
      eq(row?.IdDeal, fDeal) &&
      eq(row?.AccountNum1C, fAccount)
    );
  }, [
    data,
    pageStore.os_internal_code_filter,
    pageStore.os_payment_code_filter,
    pageStore.os_id_deal_filter,
    pageStore.os_account_num_1c_filter,
  ]);

  const handleSort = useCallback((key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  }, []);

  const sortedData = useMemo(() => {
    if (!Array.isArray(filteredData) || filteredData.length === 0) return [];
    const arr = [...filteredData];
    const { key, direction } = sortConfig;
    if (!key) return arr;

    arr.sort((a, b) => {
      const va = a?.[key];
      const vb = b?.[key];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      // числа
      if (typeof va === "number" && typeof vb === "number") {
        return direction === "asc" ? va - vb : vb - va;
      }
      // даты в формате YYYY-MM-DD
      const dateRe = /^\d{4}-\d{2}-\d{2}/;
      if (typeof va === "string" && typeof vb === "string" && dateRe.test(va) && dateRe.test(vb)) {
        return direction === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      // строки
      const sa = String(va);
      const sb = String(vb);
      return direction === "asc" ? sa.localeCompare(sb, "ru") : sb.localeCompare(sa, "ru");
    });
    return arr;
  }, [filteredData, sortConfig]);

  const formatCellValue = useCallback((value) => {
    if (value == null) return "";
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, d] = value.split("-");
      return `${d}.${m}.${y}`;
    }
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
      const [y, m, d] = value.slice(0, 10).split("-");
      return `${d}.${m}.${y}`;
    }
    return value;
  }, []);

  const renderCell = useCallback(
    (col, row) => {
      const raw = row?.[col.key];
      if (col.isLink) {
        // значение для подстановки в ссылку: либо из отдельного поля (linkField),
        // либо из самой ячейки. Для «Название» linkField = "ID" (ссылка на документ),
        // а отображаем текст названия.
        const linkValue = col.linkField ? row?.[col.linkField] : raw;
        const hasText = raw != null && raw !== "";
        const hasLink = linkValue != null && linkValue !== "";
        if (hasText && hasLink) {
          const href = (col.linkTemplate || "").replace("{value}", String(linkValue));
          return (
            <Link
              href={href}
              isExternal
              target="_blank"
              rel="noopener noreferrer"
              color="#3b82f6"
              textDecoration="underline"
              _hover={{ color: "#60a5fa" }}
              wordBreak="break-word"
            >
              {formatCellValue(raw)}
            </Link>

          );
        }
        return formatCellValue(raw);
      }
      return formatCellValue(raw);
    },
    [formatCellValue]
  );

  const exportToExcel = useCallback(() => {
    try {
      const rows = sortedData.map((row) => {
        const o = {};
        availableColumns.forEach((c) => {
          o[c.title] = row?.[c.key] ?? "";
        });
        return o;
      });
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Заказы поставщикам");
      const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(new Blob([out], { type: "application/octet-stream" }), "order_suppliers.xlsx");
    } catch (e) {
      toast({
        title: "Ошибка экспорта",
        description: String(e?.message || e),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [sortedData, availableColumns, toast]);

  const exportButton = (
    <Button
      size="sm"
      bg="#333"
      color="white"
      _hover={{ bg: "#444" }}
      onClick={exportToExcel}
      isDisabled={!sortedData.length}
    >
      Экспорт в Excel
    </Button>
  );

  // высота строки берётся из стора, как в существующих таблицах
  const rowPad = `${(pageStore.rowHeight ?? 2) * 2}px`;

  const filterInputStyle = {
    bg: "#333",
    color: "#EEEEEE",
    borderRadius: "10px",
    fontWeight: 600,
    fontSize: "14px",
    width: "180px",
    border: "none",
  };

  const anyFilterActive =
    (pageStore.os_internal_code_filter || "").trim() !== "" ||
    (pageStore.os_payment_code_filter || "").trim() !== "" ||
    (pageStore.os_id_deal_filter || "").trim() !== "" ||
    (pageStore.os_account_num_1c_filter || "").trim() !== "";

  return (
    <>
      {data.length != 0 ? (
        <VStack alignItems="flex-start" spacing={4} w="100%">
          {/* Верхнее меню — вне скролл-контейнера, уезжает при скролле страницы (как в Счетах) */}
          <ColumnFilters />

          {/* 4 фильтра по уже пришедшей таблице (точное совпадение), порядок по ТЗ:
          Внутренний код → Код оплаты в Б24 → ID сделки → Счёт */}
          <Flex
            wrap="wrap"
            gap="20px"
            align="end"
            justify="flex-start"
          >
            <VStack align="start" spacing={1}>
              <Text fontSize="14px" fontWeight="600" color="white">Внутренний код</Text>
              <Input
                {...filterInputStyle}
                placeholder="напр. NVT077"
                value={pageStore.os_internal_code_filter}
                onChange={(e) => pageStore.setOsInternalCodeFilter(e.target.value)}
              />
            </VStack>
            <VStack align="start" spacing={1}>
              <Text fontSize="14px" fontWeight="600" color="white">Код оплаты в Б24</Text>
              <Input
                {...filterInputStyle}
                placeholder="напр. PNVT29"
                value={pageStore.os_payment_code_filter}
                onChange={(e) => pageStore.setOsPaymentCodeFilter(e.target.value)}
              />
            </VStack>
            <VStack align="start" spacing={1}>
              <Text fontSize="14px" fontWeight="600" color="white">ID сделки</Text>
              <Input
                {...filterInputStyle}
                placeholder="напр. 75014"
                value={pageStore.os_id_deal_filter}
                onChange={(e) => pageStore.setOsIdDealFilter(e.target.value)}
              />
            </VStack>
            <VStack align="start" spacing={1}>
              <Text fontSize="14px" fontWeight="600" color="white">Счёт</Text>
              <Input
                {...filterInputStyle}
                placeholder="Номер счета из 1С"
                value={pageStore.os_account_num_1c_filter}
                onChange={(e) => pageStore.setOsAccountNum1CFilter(e.target.value)}
              />
            </VStack>
            {anyFilterActive && (
              <Button
                size="sm"
                variant="outline"
                color="white"
                borderColor="#555"
                _hover={{ bg: "#333" }}
                onClick={() => {
                  pageStore.setOsInternalCodeFilter("");
                  pageStore.setOsPaymentCodeFilter("");
                  pageStore.setOsIdDealFilter("");
                  pageStore.setOsAccountNum1CFilter("");
                }}
              >
                Сбросить поиск
              </Button>
            )}
          </Flex>

          <HStack justify="flex-start">
            {renderButtons ? renderButtons(exportButton) : exportButton}
            {anyFilterActive && (
              <Text fontSize="13px" color="gray.400">
                Найдено: {sortedData.length} из {Array.isArray(data) ? data.length : 0}
              </Text>
            )}
          </HStack>

          {/* Как в Счетах: большое окно (~100vh), sticky только у шапки колонок */}
          <Box
            w="100%"
            minH="100vh"
            h="100vh"
            overflow="scroll"
            borderRadius="md"
            borderWidth="1px"
            borderColor="gray.300"
          >
            <Table variant="simple" size="md" color="white">
              <Thead background="#333" position="sticky" top={0} zIndex={10}>
                <Tr>
                  {availableColumns.map((col) => {
                    const isSorted = sortConfig.key === col.key;
                    const arrow = isSorted ? (sortConfig.direction === "asc" ? " ↑" : " ↓") : "";
                    return (
                      <Th
                        key={col.key}
                        color="white"
                        cursor="pointer"
                        onClick={() => handleSort(col.key)}
                        minW={col.width || "100px"}
                        whiteSpace="nowrap"
                        _hover={{ bg: "#444" }}
                        bg="#333"
                      >
                        {col.title}
                        {arrow}
                      </Th>
                    );
                  })}
                </Tr>
              </Thead>

              <Tbody>
                {sortedData.length === 0 ? (
                  <Tr>
                    <Td colSpan={availableColumns.length} textAlign="center" color="gray.400" py={6}>
                      {Array.isArray(data) && data.length > 0
                        ? "Ничего не найдено по заданным фильтрам."
                        : "Нет данных. Нажмите «Сформировать»."}
                    </Td>
                  </Tr>
                ) : (
                  sortedData.map((row, idx) => (
                    <Tr
                      key={row?.ID ?? idx}
                      background={idx % 2 === 0 ? "rgba(26,26,26,1)" : "#333"}
                    >
                      {availableColumns.map((col) => (
                        <Td
                          key={`${row?.ID ?? idx}-${col.key}`}
                          py={rowPad}
                          width={col.width}
                          maxWidth={col.width}
                          fontSize="md"
                          whiteSpace={col.width ? "normal" : (col.key === "Title" || col.key === "Brands" ? "normal" : "nowrap")}
                          wordBreak="break-word"
                        >
                          {renderCell(col, row)}
                        </Td>
                      ))}
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </Box>
        </VStack>
      ) : null}
    </>
  );
});

export default TableData;